import toast from 'react-hot-toast'
import { TrophyIcon, SparklesIcon, FireIcon, StarIcon } from '@heroicons/react/24/solid'
import { triggerConfetti } from './ConfettiEffect'

// Custom toast component for badge unlocks
const BadgeToast = ({ badge }) => (
  <div className="flex items-center space-x-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-4 rounded-lg shadow-lg border-2 border-yellow-300">
    <div className="flex-shrink-0">
      <div className="relative">
        <TrophyIcon className="h-10 w-10 animate-bounce" />
        <SparklesIcon className="h-4 w-4 absolute -top-1 -right-1 animate-ping" />
      </div>
    </div>
    <div className="flex-1">
      <p className="font-bold text-lg">Badge Unlocked! 🎉</p>
      <p className="text-sm font-semibold">{badge.icon} {badge.name}</p>
      <p className="text-xs opacity-90">+{badge.points} points</p>
    </div>
  </div>
)

// Custom toast for milestone achievements
const MilestoneToast = ({ title, message, emoji }) => (
  <div className="flex items-center space-x-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white p-4 rounded-lg shadow-lg border-2 border-purple-300">
    <div className="flex-shrink-0">
      <div className="relative">
        <StarIcon className="h-10 w-10 animate-spin" style={{ animationDuration: '3s' }} />
      </div>
    </div>
    <div className="flex-1">
      <p className="font-bold text-lg">{emoji} {title}</p>
      <p className="text-sm">{message}</p>
    </div>
  </div>
)

// Custom toast for completion
const CompletionToast = ({ title, subtitle }) => (
  <div className="flex items-center space-x-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-lg shadow-lg border-2 border-green-300">
    <div className="flex-shrink-0">
      <div className="relative">
        <FireIcon className="h-10 w-10 animate-pulse" />
      </div>
    </div>
    <div className="flex-1">
      <p className="font-bold text-lg">✅ {title}</p>
      <p className="text-sm">{subtitle}</p>
    </div>
  </div>
)

// Achievement notification functions
export const achievementToast = {
  // Show badge unlock notification
  badge: (badge, options = {}) => {
    triggerConfetti('stars')
    toast.custom((t) => <BadgeToast badge={badge} />, {
      duration: 5000,
      position: 'top-center',
      ...options
    })
  },

  // Show milestone notification (25%, 50%, 75%)
  milestone: (percentage, options = {}) => {
    const milestones = {
      25: { title: 'Quarter Way There!', message: 'Great start! Keep the momentum going! 💪', emoji: '🌟', confetti: 'basic' },
      50: { title: 'Halfway Point!', message: 'You\'re crushing it! The finish line is in sight! 🎯', emoji: '⭐', confetti: 'fireworks' },
      75: { title: 'Almost Done!', message: 'Final stretch! You\'re so close to mastery! 🚀', emoji: '🔥', confetti: 'fireworks' },
      100: { title: 'Course Complete!', message: 'You\'re a certified AI expert! Download your certificate! 🎓', emoji: '🎉', confetti: 'realistic' }
    }

    const milestone = milestones[percentage]
    if (milestone) {
      triggerConfetti(milestone.confetti)
      toast.custom((t) => <MilestoneToast {...milestone} />, {
        duration: 6000,
        position: 'top-center',
        ...options
      })
    }
  },

  // Show module completion
  moduleComplete: (moduleName, options = {}) => {
    triggerConfetti('basic')
    toast.custom((t) => <CompletionToast title="Module Complete!" subtitle={moduleName} />, {
      duration: 4000,
      position: 'top-center',
      ...options
    })
  },

  // Show lab completion
  labComplete: (labName, options = {}) => {
    triggerConfetti('cannons')
    toast.custom((t) => <CompletionToast title="Lab Complete!" subtitle={labName} />, {
      duration: 4000,
      position: 'top-center',
      ...options
    })
  },

  // Show capstone completion
  capstoneComplete: (capstoneName, options = {}) => {
    triggerConfetti('realistic')
    toast.custom((t) => <CompletionToast title="Capstone Complete!" subtitle={capstoneName} />, {
      duration: 5000,
      position: 'top-center',
      ...options
    })
  },

  // Generic success notification
  success: (message, options = {}) => {
    toast.success(message, {
      duration: 3000,
      position: 'top-right',
      style: {
        background: '#10b981',
        color: '#fff',
        fontWeight: '600',
      },
      ...options
    })
  },

  // Generic error notification
  error: (message, options = {}) => {
    toast.error(message, {
      duration: 4000,
      position: 'top-right',
      style: {
        background: '#ef4444',
        color: '#fff',
        fontWeight: '600',
      },
      ...options
    })
  }
}

export default achievementToast
